/**
 * Плагин: клик по ячейке — поднять узел на передний план, показать tools у edge;
 * подсветка связей: при клике по node — подсветить связанные edges, при клике по edge — подсветить его;
 * blank:click — сброс tools и цвета связей в обычный чёрный;
 * node:mousedown — перенос Transform-рамки и selection на перетаскиваемую ноду (кроме группового переноса).
 */

/**
 * Сводка по связи для отладки (клик по edge).
 * @param {import('@antv/x6').Edge} edge
 * @param {import('../ModelerX6').default} modeler
 */
function logEdgeClickDetails(edge, modeler) {
	try {
		const viewElement =
			typeof modeler?._findViewElementById === 'function'
				? modeler._findViewElementById(edge.id)
				: null;
		const x6Json = typeof edge?.toJSON === 'function' ? edge.toJSON() : null;
		const lineAttr =
			typeof edge?.getAttrByPath === 'function'
				? edge.getAttrByPath('line')
				: typeof edge?.attr === 'function'
					? edge.attr('line')
					: null;
		const relationshipType =
			viewElement && typeof modeler?._getRelationshipType === 'function'
				? modeler._getRelationshipType(viewElement)
				: null;
		const accessType =
			viewElement && typeof modeler?._getRelationshipAccessType === 'function'
				? modeler._getRelationshipAccessType(viewElement)
				: null;
		const bbox = typeof edge?.getBBox === 'function' ? edge.getBBox() : null;
		const payload = {
			edgeId: edge?.id,
			x6: x6Json,
			lineAttr,
			bbox: bbox
				? {
						x: bbox.x,
						y: bbox.y,
						width: bbox.width,
						height: bbox.height,
					}
				: null,
			router: typeof edge?.getRouter === 'function' ? edge.getRouter() : null,
			connector: typeof edge?.getConnector === 'function' ? edge.getConnector() : null,
			vertices: typeof edge?.getVertices === 'function' ? edge.getVertices() : null,
			labels: typeof edge?.getLabels === 'function' ? edge.getLabels() : null,
			sourceCellId:
				typeof edge?.getSourceCellId === 'function' ? edge.getSourceCellId() : null,
			targetCellId:
				typeof edge?.getTargetCellId === 'function' ? edge.getTargetCellId() : null,
			source: typeof edge?.getSource === 'function' ? edge.getSource() : null,
			target: typeof edge?.getTarget === 'function' ? edge.getTarget() : null,
			relationshipType,
			accessType,
			viewElement,
		};
		// eslint-disable-next-line no-console
		console.groupCollapsed('[ArchiX6] Клик по связи', edge?.id ?? '');
		// eslint-disable-next-line no-console
		console.log('Сводка:', payload);
		// eslint-disable-next-line no-console
		console.log('X6 toJSON():', x6Json);
		// eslint-disable-next-line no-console
		console.log('View element (модель диаграммы):', viewElement);
		// eslint-disable-next-line no-console
		console.groupEnd();
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[ArchiX6] logEdgeClickDetails failed', err);
	}
}

/**
 * Сводка по узлу для отладки (клик по node).
 * @param {import('@antv/x6').Node} node
 * @param {import('../ModelerX6').default} modeler
 * @param {import('@antv/x6').Graph} graph
 */
function logNodeClickDetails(node, modeler, graph) {
	try {
		const viewElement =
			typeof modeler?._findViewElementById === 'function'
				? modeler._findViewElementById(node.id)
				: null;
		const elementType =
			viewElement && typeof modeler?._getType === 'function'
				? modeler._getType(viewElement)
				: null;
		const label =
			viewElement && typeof modeler?._getLabel === 'function'
				? modeler._getLabel(viewElement)
				: null;
		const x6Json = typeof node?.toJSON === 'function' ? node.toJSON() : null;
		const bbox = typeof node?.getBBox === 'function' ? node.getBBox() : null;
		const nodeId = node?.id;
		const incidentEdgeIds = [];
		const edges = graph?.getEdges?.() ?? [];
		edges.forEach(edge => {
			const src =
				edge.getSourceCellId?.() ?? edge.getSource?.()?.cell ?? edge.getSourceCell?.()?.id;
			const tgt =
				edge.getTargetCellId?.() ?? edge.getTarget?.()?.cell ?? edge.getTargetCell?.()?.id;
			const srcId = typeof src === 'string' ? src : (src?.id ?? null);
			const tgtId = typeof tgt === 'string' ? tgt : (tgt?.id ?? null);
			if (srcId === nodeId || tgtId === nodeId) incidentEdgeIds.push(edge.id);
		});
		const payload = {
			nodeId,
			elementType,
			label,
			x6: x6Json,
			bbox: bbox
				? {
						x: bbox.x,
						y: bbox.y,
						width: bbox.width,
						height: bbox.height,
					}
				: null,
			position: typeof node?.getPosition === 'function' ? node.getPosition() : null,
			size: typeof node?.getSize === 'function' ? node.getSize() : null,
			angle: typeof node?.getAngle === 'function' ? node.getAngle() : null,
			parent:
				typeof node?.getParentId === 'function'
					? node.getParentId()
					: typeof node?.getParent === 'function'
						? (node.getParent()?.id ?? null)
						: null,
			ports: typeof node?.getPorts === 'function' ? node.getPorts() : null,
			incidentEdgeIds,
			viewElement,
		};
		// eslint-disable-next-line no-console
		console.groupCollapsed('[ArchiX6] Клик по узлу', node?.id ?? '');
		// eslint-disable-next-line no-console
		console.log('Сводка:', payload);
		// eslint-disable-next-line no-console
		console.log('X6 toJSON():', x6Json);
		// eslint-disable-next-line no-console
		console.log('View element (модель диаграммы):', viewElement);
		// eslint-disable-next-line no-console
		console.groupEnd();
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[ArchiX6] logNodeClickDetails failed', err);
	}
}

const HIGHLIGHT_STROKE = '#1677FF';
const DEFAULT_STROKE = '#000000';

export default {
	init(modeler, context) {
		const {graph} = context;

		const bringNodeToFront = cell => {
			if (
				cell &&
				typeof cell.isNode === 'function' &&
				cell.isNode() &&
				typeof cell.toFront === 'function'
			) {
				cell.toFront({deep: true});
			}
			const edges = graph.getEdges?.() ?? [];
			edges.forEach(edge => {
				if (typeof edge.toFront === 'function') edge.toFront();
			});
		};

		const removeEdgeToolsFromAll = () => {
			graph.getEdges?.()?.forEach(edge => {
				const view = graph.findViewByCell?.(edge);
				if (view?.removeTools) view.removeTools();
			});
		};

		/** Квадрат 4×4: source — сдвиг 1 к ноде (-3..1), target — 2 к ноде (0..4). */
		const getArrowheadToolAttrs = (edge, type) => {
			const line = edge.getAttr?.('line') ?? edge.attr?.('line') ?? {};
			const stroke = line.stroke ?? '#000000';
			const pathSource = 'M -3 -2 L 1 -2 L 1 2 L -3 2 Z';
			const pathTarget = 'M 0 -2 L 4 -2 L 4 2 L 0 2 Z';
			return {
				d: type === 'source' ? pathSource : pathTarget,
				fill: stroke,
				stroke: stroke,
				'stroke-width': 1,
				cursor: 'move',
			};
		};

		/** Концы ребра (ratio 0 и 1): квадрат центрирован, поэтому половина заходит на ноду. */
		const SOURCE_ARROWHEAD_RATIO = 0;
		const TARGET_ARROWHEAD_RATIO = 1;

		/**
		 * У manhattan-роутера ручное перетаскивание вершин/сегментов ломает маршрут и даёт fallback на orth.
		 * Оставляем только стрелки концов (переподключение source/target).
		 */
		const isManhattanRouting = () =>
			typeof modeler?.getConnectionRouter === 'function' &&
			modeler.getConnectionRouter() === 'manhattan';

		const addEdgeTools = edge => {
			const view = graph.findViewByCell?.(edge);
			if (!view?.addTools) return;
			const arrowItems = [
				{
					name: 'source-arrowhead',
					args: {
						attrs: getArrowheadToolAttrs(edge, 'source'),
						ratio: SOURCE_ARROWHEAD_RATIO,
					},
				},
				{
					name: 'target-arrowhead',
					args: {
						attrs: getArrowheadToolAttrs(edge, 'target'),
						ratio: TARGET_ARROWHEAD_RATIO,
					},
				},
			];
			const vertexSegmentItems = [
				{name: 'vertices', args: {addable: true, removable: true}},
				{name: 'segments'},
			];
			const items = isManhattanRouting()
				? arrowItems
				: [...vertexSegmentItems, ...arrowItems];
			view.addTools({items});
		};

		/** Возвращает Set id рёбер, которые нужно подсветить: при клике по node — инцидентные, по edge — само ребро. */
		const getEdgeIdsToHighlight = cell => {
			if (!cell) return new Set();
			const edges = graph.getEdges?.() ?? [];
			if (typeof cell.isEdge === 'function' && cell.isEdge()) {
				return new Set([cell.id]);
			}
			if (typeof cell.isNode === 'function' && cell.isNode()) {
				const nodeId = cell.id;
				const ids = new Set();
				edges.forEach(edge => {
					const src =
						edge.getSourceCellId?.() ??
						edge.getSource?.()?.cell ??
						edge.getSourceCell?.()?.id;
					const tgt =
						edge.getTargetCellId?.() ??
						edge.getTarget?.()?.cell ??
						edge.getTargetCell?.()?.id;
					const srcId = typeof src === 'string' ? src : (src?.id ?? null);
					const tgtId = typeof tgt === 'string' ? tgt : (tgt?.id ?? null);
					if (srcId === nodeId || tgtId === nodeId) ids.add(edge.id);
				});
				return ids;
			}
			return new Set();
		};

		/** Обновить stroke у маркера (object), если у него есть свой stroke (например Realization — path с контуром). */
		const withMarkerStroke = (marker, stroke) => {
			if (!marker || typeof marker !== 'object') return marker;
			return {...marker, stroke};
		};

		/** Подсветка: edgeIdsToHighlight — Set id рёбер для цвета #1677FF, остальные — #000000. Линия + контур маркеров. */
		const applyEdgeHighlight = edgeIdsToHighlight => {
			const edges = graph.getEdges?.() ?? [];
			edges.forEach(edge => {
				const stroke = edgeIdsToHighlight.has(edge.id) ? HIGHLIGHT_STROKE : DEFAULT_STROKE;
				const line =
					edge.getAttrByPath?.('line') ??
					(typeof edge.attr === 'function' ? edge.attr('line') : null);
				if (!line || typeof line !== 'object') {
					if (typeof edge.setAttrByPath === 'function')
						edge.setAttrByPath('line/stroke', stroke);
					else if (typeof edge.attr === 'function') edge.attr('line/stroke', stroke);
					return;
				}
				const next = {
					...line,
					stroke,
					sourceMarker: withMarkerStroke(line.sourceMarker, stroke),
					targetMarker: withMarkerStroke(line.targetMarker, stroke),
				};
				if (typeof edge.attr === 'function') edge.attr('line', next);
				else if (typeof edge.setAttrByPath === 'function') {
					edge.setAttrByPath('line/stroke', stroke);
					if (next.sourceMarker && typeof next.sourceMarker === 'object') {
						edge.setAttrByPath('line/sourceMarker', next.sourceMarker);
					}
					if (next.targetMarker && typeof next.targetMarker === 'object') {
						edge.setAttrByPath('line/targetMarker', next.targetMarker);
					}
				}
			});
		};

		const onCellClick = ({cell}) => {
			bringNodeToFront(cell);
			if (cell && typeof cell.isEdge === 'function' && cell.isEdge()) {
				logEdgeClickDetails(cell, modeler);
				graph.clearTransformWidgets?.();
				removeEdgeToolsFromAll();
				addEdgeTools(cell);
			} else if (cell && typeof cell.isNode === 'function' && cell.isNode()) {
				logNodeClickDetails(cell, modeler, graph);
				removeEdgeToolsFromAll();
			} else {
				removeEdgeToolsFromAll();
			}
			applyEdgeHighlight(getEdgeIdsToHighlight(cell));
		};

		const onBlankClick = () => {
			removeEdgeToolsFromAll();
			applyEdgeHighlight(new Set());
		};

		const isMultiSelectModifier = e =>
			Boolean(e && (e.ctrlKey || e.metaKey || e.shiftKey));

		/** Групповой перенос (рамка Selection / box-select): не сбрасываем выделение на одну ноду. */
		const shouldKeepGroupSelection = node => {
			const selectedCells =
				typeof graph.getSelectedCells === 'function'
					? graph.getSelectedCells().filter(c => c?.isNode?.())
					: [];
			return selectedCells.length > 1 && selectedCells.some(c => c.id === node.id);
		};

		/** Transform-рамка (X6) вешается на node:click; при drag другой ноды переносим активность сюда. */
		const activateNodeForInteraction = (node, e) => {
			if (!node?.id) return;
			if (isMultiSelectModifier(e)) return;
			if (shouldKeepGroupSelection(node)) return;

			if (typeof graph.resetSelection === 'function') {
				graph.resetSelection(node);
			}

			if (typeof graph.createTransformWidget === 'function') {
				graph.createTransformWidget(node);
			}

			applyEdgeHighlight(getEdgeIdsToHighlight(node));
		};

		let moveToFrontDone = new Set();
		const onNodeMouseDown = ({node, e}) => {
			activateNodeForInteraction(node, e);
		};
		const onNodeMove = ({node}) => {
			if (!moveToFrontDone.has(node.id)) {
				moveToFrontDone.add(node.id);
				bringNodeToFront(node);
			}
		};
		const onNodeMoved = ({node}) => moveToFrontDone.delete(node.id);

		graph.on('cell:click', onCellClick);
		graph.on('blank:click', onBlankClick);
		graph.on('node:mousedown', onNodeMouseDown);
		graph.on('node:move', onNodeMove);
		graph.on('node:moved', onNodeMoved);

		return () => {
			graph.off('cell:click', onCellClick);
			graph.off('blank:click', onBlankClick);
			graph.off('node:mousedown', onNodeMouseDown);
			graph.off('node:move', onNodeMove);
			graph.off('node:moved', onNodeMoved);
		};
	},
};
